/**
 * Authentication screen with two modes:
 *  - Sign in (any role)
 *  - Register (self-service client account → creates User + Client profile)
 *
 * On success the user lands on the page they originally requested, or the
 * dashboard. Field-level validation is handled by Zod + react-hook-form.
 */
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, useNavigate } from "react-router-dom";
import { Factory, LogIn, UserPlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button, TextField } from "../components/ui/Form";
import { toast } from "../components/ui/Toast";
import { getErrorMessage } from "../lib/errors";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});
type LoginValues = z.infer<typeof loginSchema>;

const registerSchema = z.object({
  fullName: z.string().min(2, "Your name is required"),
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "At least 8 characters"),
  companyName: z.string().min(2, "Company name is required"),
  phone: z.string().optional(),
  city: z.string().optional(),
});
type RegisterValues = z.infer<typeof registerSchema>;

export const Login: React.FC = () => {
  const [mode, setMode] = useState<"login" | "register">("login");
  const { login, register: registerClient } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
    "/dashboard";

  const loginForm = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });
  const registerForm = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
  });

  const onLogin = loginForm.handleSubmit(async (values) => {
    try {
      const user = await login(values);
      toast.success(`Welcome back, ${user.fullName.split(" ")[0]}!`);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  const onRegister = registerForm.handleSubmit(async (values) => {
    try {
      const user = await registerClient(values);
      toast.success(`Account created — welcome, ${user.fullName.split(" ")[0]}!`);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg">
            <Factory size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-100">Factory MIS</h1>
          <p className="mt-1 text-sm text-slate-400">
            Tissue manufacturing management system
          </p>
        </div>

        <div className="card p-6">
          <div className="mb-6 grid grid-cols-2 gap-1 rounded-lg bg-slate-900 p-1">
            <button
              onClick={() => setMode("login")}
              className={tab(mode === "login")}
            >
              Sign in
            </button>
            <button
              onClick={() => setMode("register")}
              className={tab(mode === "register")}
            >
              Register
            </button>
          </div>

          {mode === "login" ? (
            <form onSubmit={onLogin} className="space-y-4" noValidate>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                error={loginForm.formState.errors.email?.message}
                {...loginForm.register("email")}
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                error={loginForm.formState.errors.password?.message}
                {...loginForm.register("password")}
              />
              <Button
                type="submit"
                className="w-full"
                loading={loginForm.formState.isSubmitting}
                icon={<LogIn size={18} />}
              >
                Sign in
              </Button>
            </form>
          ) : (
            <form onSubmit={onRegister} className="space-y-4" noValidate>
              <TextField
                label="Full name"
                placeholder="Jane Doe"
                error={registerForm.formState.errors.fullName?.message}
                {...registerForm.register("fullName")}
              />
              <TextField
                label="Company name"
                placeholder="ABC Distributors"
                error={registerForm.formState.errors.companyName?.message}
                {...registerForm.register("companyName")}
              />
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                error={registerForm.formState.errors.email?.message}
                {...registerForm.register("email")}
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                error={registerForm.formState.errors.password?.message}
                {...registerForm.register("password")}
              />
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label="Phone"
                  placeholder="Optional"
                  {...registerForm.register("phone")}
                />
                <TextField
                  label="City"
                  placeholder="Optional"
                  {...registerForm.register("city")}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                loading={registerForm.formState.isSubmitting}
                icon={<UserPlus size={18} />}
              >
                Create account
              </Button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          Internal staff accounts are provisioned by an administrator.
        </p>
      </div>
    </div>
  );
};

const tab = (active: boolean) =>
  `rounded-md py-2 text-sm font-medium transition-colors ${
    active ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"
  }`;
